import { useCallback } from 'react';
import type { ItemSlotTarget } from '@sorrem/shared';
import { resolveApiErrorMessage, useMoveCharacterItemMutation } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';

export interface ItemMoveState {
    /** Saves a move of one of the character's items. Resolves to why the server refused it, or null once saved. */
    move: (itemId: string, target: ItemSlotTarget) => Promise<string | null>;
}

/** Saving where a character's items go, through the API (which refreshes the cached characters). */
export function useItemMove(characterId: string | null): ItemMoveState {
    // Same cache entry as useCurrentPlayerCharacter, which keys it by the lower-cased account id.
    const accountId = getStoredCanonicalAccountId();
    const [moveItem] = useMoveCharacterItemMutation();

    const move = useCallback(async (itemId: string, target: ItemSlotTarget): Promise<string | null> => {
        if (accountId === null || characterId === null) {
            return 'You need to be signed in to move items.';
        }
        try {
            await moveItem({ accountId, characterId, itemId, target }).unwrap();
            return null;
        } catch (failure) {
            return resolveApiErrorMessage(failure, 'The move could not be saved.');
        }
    }, [accountId, characterId, moveItem]);

    return { move };
}
